'use client'
import { useEffect, useState } from "react"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { ErrorMessage, Field, Form, Formik } from 'formik'
import AuthBottom from "../AuthBottom"
import AuthTop from "../AuthTop"
import PrimaryButton from "@/Component/Common/Forms/PrimaryButton"
import { LoaderSVG } from "@/Component/Common/SVGs/CommonSVG"
import { RequiredField, YupObject } from "@/Utils/ValidationSchema"
import { useTranslation } from "@/Utils/Hooks/useTranslate"
import ValidationMsg from "@/Utils/ValidationSchema/ValidationMsg"
import { useLoading } from "@/Utils/Hooks/useLoading"
import SkeletonLoader from "@/Layout/SkeletonLoader"
import { BrandsMenuSVG } from "@/Component/Common/SVGs/SidebarSVG"

const Register = () => {
    const router = useRouter();
    const [isPending, setIsPending] = useState(false);
    const { translations } = useTranslation();
    const { skeletonLoading, startSkeletonLoading, stopSkeletonLoading } = useLoading();

    useEffect(() => {
        startSkeletonLoading();
        setTimeout(() => {
            stopSkeletonLoading();
        }, 1000);
    }, [])

    const handleSubmit = (values: any) => {
        setIsPending(true)
        setTimeout(() => {
            setIsPending(false)
            router.push(`/register/${values.role}`)
        }, 1000)
    }

    return (
        <div className='mx-auto max-w-xl flex flex-col h-full'>
            <AuthTop skeletonLoading={skeletonLoading} logoutBtn={false} translations={translations} />
            <span className="kicker mb-3">{translations?.createAccount || 'Create your account'}</span>
            <h1 className='text-display font-bold lg:text-4xl md:text-3xl sm:text-2xl text-xl sm:mb-8 mb-5 !leading-[1.05] text-neutral-900'>{skeletonLoading ? <SkeletonLoader height={60} rows={1} /> : translations.joinAs}</h1>
            <Formik
                initialValues={{ role: '' }}
                validationSchema={YupObject({ role: RequiredField(translations) })}
                onSubmit={handleSubmit}
            >
                {({ errors, touched }) => (
                    <Form className='flex-1'>
                        <div className="grid grid-cols-2 gap-5 xs:mb-7 mb-5">

                            {skeletonLoading ? <SkeletonLoader height={186} rows={1} /> :
                                <label className="relative cursor-pointer">
                                    <Field type="radio" name="role" value="brand" className="hidden peer" />
                                    <div className="sm:h-48 xs:h-40 h-36 w-full border border-neutral-200 bg-white peer-checked:border-transparent peer-checked:bg-gradient-to-br peer-checked:from-primary peer-checked:to-accent p-[2px] rounded-card group transition-all shadow-soft hover:shadow-float hover:-translate-y-0.5 after:content-[''] after:absolute after:top-5 after:right-5 after:h-5 after:w-5 after:border after:border-neutral-300 after:bg-white after:transition-all after:rounded-full before:content-[''] before:absolute before:top-6 before:right-6 before:h-3 before:w-3 before:bg-gradient-to-br before:from-primary before:to-accent before:hidden before:transition-all before:rounded-full peer-checked:before:block peer-checked:after:border-white">
                                        <div className="flex h-full w-full bg-white rounded-[calc(var(--tw-border-radius,20px))] rounded-card flex-col xs:p-5 p-3 justify-between">
                                            <div className="flex items-start justify-between text-4xl">💼</div>
                                            <span className="text-display sm:text-2xl xs:text-xl font-bold text-neutral-900">{translations.brand}</span>
                                        </div>
                                    </div>
                                </label>
                            }
                            {skeletonLoading ? <SkeletonLoader height={186} rows={1} /> :
                                <label className="relative cursor-pointer">
                                    <Field type="radio" name="role" value="influencer" className="hidden peer" />
                                    <div className="sm:h-48 xs:h-40 h-36 w-full border border-neutral-200 bg-white peer-checked:border-transparent peer-checked:bg-gradient-to-br peer-checked:from-primary peer-checked:to-accent p-[2px] rounded-card group transition-all shadow-soft hover:shadow-float hover:-translate-y-0.5 after:content-[''] after:absolute after:top-5 after:right-5 after:h-5 after:w-5 after:border after:border-neutral-300 after:bg-white after:transition-all after:rounded-full before:content-[''] before:absolute before:top-6 before:right-6 before:h-3 before:w-3 before:bg-gradient-to-br before:from-primary before:to-accent before:hidden before:transition-all before:rounded-full peer-checked:before:block peer-checked:after:border-white">
                                        <div className="flex h-full w-full bg-white rounded-card flex-col xs:p-5 p-3 justify-between">
                                            <div className="flex items-start justify-between text-4xl">✨</div>
                                            <span className="text-display sm:text-2xl xs:text-xl font-bold text-neutral-900">{translations.influencer}</span>
                                        </div>
                                    </div>
                                </label>
                            }
                        </div>
                        <ErrorMessage name="role" component="div" render={() =>
                            <ValidationMsg errorMsg={translations.roleRequired} />
                        } />
                        <PrimaryButton customClass='w-full xs:mt-7 mt-4 flex items-center justify-center !py-3' buttonType='submit' disabled={isPending} skeletonLoading={skeletonLoading} width={'100%'}>
                            {translations.createAccount}
                            {isPending && <LoaderSVG customClass='ml-2.5' />}
                        </PrimaryButton>
                        <AuthBottom title={translations.haveAccount} link="/login" linkText={translations.loginNow} skeletonLoading={skeletonLoading} />
                    </Form>
                )}
            </Formik>

        </div>
    )
}

export default Register
